import styled from '@emotion/styled'
import React, { HTMLAttributes, useState } from 'react'

import { Card } from '../Card'
import { OptionProps, useInputOptions } from '../Hooks/useInputOptions'
import { Icon } from '../Icon'
import { PopOut } from '../PopOut'
import { InputProps, TextInput } from '../TextInput'
import { themeColor, themeFont } from '../Theme/utils'

type Props = InputProps

type SafeHTMLProps = Omit<HTMLAttributes<HTMLSelectElement>, keyof OptionProps>

type SelectProps = Props &
  OptionProps &
  SafeHTMLProps & {
    placeholder?: string
    name?: string
    isParentSelectorDisabled?: boolean
    width?: number
  }

export const Select = ({
  placeholder,
  isParentSelectorDisabled,
  width,
  ...props
}: SelectProps) => {
  const [visible, setVisible] = useState<boolean>(false)

  const { options, selected } = useInputOptions(props)

  const { options: _, onChange, ...filteredProps } = props

  const inputProps: InputProps & { value?: string } = {
    ...filteredProps,
    value: selected?.label || placeholder,
  }
  return (
    <PopOut
      visible={visible}
      width={width}
      onClose={() => setVisible(false)}
      closeOnClick
      underlay
      align="left"
      trigger="click"
      isNotFixed
      content={
        <OptionsCard mt={3} border shadow py={1} width={width}>
          {options.map(
            ({ label, value: val, isParentSection = false, indent }) => (
              <Option
                key={val}
                value={val}
                indent={indent}
                onClick={() => {
                  if (!isParentSection) {
                    onChange(val)
                  }
                }}
                isParentSection={isParentSection}
                isParentSelectorDisabled={isParentSelectorDisabled}
                disabled={isParentSection}>
                {label}
              </Option>
            ),
          )}
        </OptionsCard>
      }>
      <Wrapper width={width}>
        <TextInput
          {...inputProps}
          readOnly
          width={width}
          onClick={() => setVisible(!visible)}
        />
        <ChevronContainer>
          <Chevron
            open={visible}
            icon="chevron-down"
            color="typography.2"
            onClick={() => setVisible(!visible)}
          />
        </ChevronContainer>
      </Wrapper>
    </PopOut>
  )
}

const Wrapper = styled.div<{ width: number | undefined }>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  > input {
    padding-right: 32px;
  }
`
const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 16px;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
`

const Chevron = styled(Icon)<{ open: boolean }>`
  transform: rotate(${props => (props.open ? '180deg' : '0deg')});
  transition: background 0.3s ease, transform 0.3s ease;
`

const OptionsCard = styled(Card)<{ width: number | undefined }>`
  background: ${themeColor('surfaces.4')};
  flex-direction: column;
  max-height: 264px;
  overflow-y: auto;
  width: ${({ width }) => (width ? `${width}px` : '100%')};
`
const Option = styled.button<{
  isParentSection: boolean
  isParentSelectorDisabled?: boolean
  indent: number | undefined
}>`
  display: flex;
  font-family: ${themeFont};
  font-size: 16px;
  color: ${themeColor('typography.1')};
  border: none;
  outline: none;
  padding: 8px 16px;
  background: transparent;
  transition: background 0.3s ease;
  align-items: flex-start;
  border-radius: inherit;
  margin-left: ${({ indent }) => `${indent}px`};
  font-weight: ${({ isParentSection = false }) =>
    isParentSection ? 700 : 300};
  cursor: ${({ isParentSection }) => (isParentSection ? 'default' : 'pointer')};
  :hover {
    background: ${({ isParentSection }) =>
      isParentSection ? null : themeColor('surfaces.2')};
  }
`
